import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Loader,
  AppError,
  EmptyState,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Tag,
  Modal,
  type TagTone,
} from "@bfc-software/pallet";
import { useContext, useMemo } from "react";
import { TFunction } from "i18next";
import { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";
import { AuthContext, AuthUtils } from "@bfc-software/app-toolbox";
import { UseQueryResult } from "@tanstack/react-query";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";

import styles from "./load-plans.module.css";

type LoadPlanHistory =
  TaskPlannerSchemaTypes.components["schemas"]["LoadPlanHistory"];

type LoadPlanHistoryItem =
  TaskPlannerSchemaTypes.components["schemas"]["LoadPlanHistoryItem"];

type LoadPlanStatus = LoadPlanHistoryItem["status"];

/** The history listing defaults to showing only failed rows. */
const DEFAULT_STATUS: LoadPlanStatus = "errored";

const COLUMN_COUNT = 6;

const STATUS_TONES: Record<LoadPlanStatus, TagTone> = {
  complete: "success",
  queued: "neutral",
  errored: "danger",
};

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/task-plans/load-plans/",
)({
  staticData: {
    crumb: (_: unknown, t: TFunction) => {
      return { label: t("page.loadPlans.crumb") };
    },
  },
  component: RouteComponent,
});

export function ErrorsModal({
  errors,
  t,
}: {
  errors: NonNullable<LoadPlanHistoryItem["errors"]>;
  t: TFunction;
}) {
  const summary = errors
    .map((error) => error.message ?? error.code)
    .filter((value): value is string => typeof value === "string")
    .join(", ");

  return (
    <Modal
      trigger={{
        variant: "tertiary",
        display: { scale: "inline" },
        text: summary,
        className: styles.errorTrigger,
        "aria-label": t("page.loadPlans.errorsModal.trigger"),
      }}
      header={t("page.loadPlans.errorsModal.title", { count: errors.length })}
    >
      <div className={styles.errorList} data-test-role="load-plan-error-list">
        {errors.map((error, index) => (
          <div
            key={index}
            className={styles.errorRow}
            data-test-role="load-plan-error-row"
          >
            {error.code ? (
              <span className={styles.errorRowCode}>{error.code}</span>
            ) : null}
            <span>{error.message}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function ErrorsCell({
  errors,
  t,
}: {
  errors: LoadPlanHistoryItem["errors"];
  t: TFunction;
}) {
  return (
    <TableCell>
      {errors && errors.length > 0 ? (
        <ErrorsModal errors={errors} t={t} />
      ) : null}
    </TableCell>
  );
}

function LoadPlanRow({ item, t }: { item: LoadPlanHistoryItem; t: TFunction }) {
  const { org, location, lang } = Route.useParams();

  return (
    <TableRow data-test-role="load-plan-row">
      <TableCell>
        <DateValue dateValue={item.created_at} />
      </TableCell>
      <TableCell>
        <Tag
          label={item.status}
          display={{ tone: STATUS_TONES[item.status] }}
        />
      </TableCell>
      <TableCell>{item.event_type}</TableCell>
      <TableCell>{item.warehouse_ext_id}</TableCell>
      <TableCell>
        {item.task_plan_id ? (
          <Link
            to="/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan"
            params={{ "task-plan": item.task_plan_id, org, location, lang }}
          >
            {item.task_plan_id}
          </Link>
        ) : (
          <></>
        )}
      </TableCell>
      <ErrorsCell errors={item.errors} t={t} />
    </TableRow>
  );
}

function LoadPlanTable({
  t,
  children,
}: {
  t: TFunction;
  children: React.ReactNode;
}) {
  return (
    <Table aria-label={t("page.loadPlans.table.ariaLabel")} isResizable={false}>
      <TableHead>
        <TableHeaderCell isRowHeader={true}>
          {t("page.loadPlans.table.created")}
        </TableHeaderCell>
        <TableHeaderCell>{t("page.loadPlans.table.status")}</TableHeaderCell>
        <TableHeaderCell>{t("page.loadPlans.table.eventType")}</TableHeaderCell>
        <TableHeaderCell>{t("page.loadPlans.table.warehouse")}</TableHeaderCell>
        <TableHeaderCell>{t("page.loadPlans.table.taskPlan")}</TableHeaderCell>
        <TableHeaderCell>{t("page.loadPlans.table.errors")}</TableHeaderCell>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

function LoadPlannerHistory<T>({
  query,
  t,
}: {
  query: UseQueryResult<LoadPlanHistory, T>;
  t: TFunction;
}) {
  if (query.isError) {
    return (
      <TableRow>
        <TableCell colSpan={COLUMN_COUNT}>
          <AppError
            description={t("page.loadPlans.failed")}
            resolution={t("page.loadPlans.failureSupport")}
          />
        </TableCell>
      </TableRow>
    );
  }

  const loadPlans = query.data?.load_plans ?? [];
  if (loadPlans.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={COLUMN_COUNT}>
          <EmptyState heading={t("page.loadPlans.empty")} />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {loadPlans.map((item) => (
        <LoadPlanRow key={item.id} item={item} t={t} />
      ))}
    </>
  );
}

function RouteComponent() {
  const { org, location, lang } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const client = useMemo(() => apiClient(), []);

  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const tenantId = AuthUtils.getTenantId(authState, orgId, location);

  const query = client.useQuery(
    "get",
    "/{orgId}/load-plan-history",
    {
      params: {
        path: { orgId: orgId! },
        query: { status: DEFAULT_STATUS },
      },
    },
    { enabled: typeof orgId === "string" },
  );

  return (
    <div
      className={palletOverrides.basicPageContent}
      data-tenant-id={tenantId}
      data-org-id={orgId}
    >
      {query.isPending ? (
        <div className={styles.loadingContainer}>
          <Loader type="page" />
        </div>
      ) : (
        <LoadPlanTable t={t}>
          <LoadPlannerHistory query={query} t={t} />
        </LoadPlanTable>
      )}
    </div>
  );
}
